import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import Loader from '../components/Loader';
import styles from './BusinessProfilePopup.module.css';
import PortfolioLightbox from './PortfolioLightbox';
import { X, CheckCircle, ListChecks, Award, Star, Phone, Lock, Globe, MessageSquare, MapPin, Bookmark, Mail, Share2, Clock, Sun, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api/api.js';
import VerifiedPill from '../components/VerifiedBadge/VerifiedPill.jsx';

// ... (Helper functions getInitials, pastelColors, getColorForUser, timeSince, Review component remain the same) ...
const getInitials = (firstName, lastName) => {
    const firstInitial = firstName ? firstName.charAt(0) : '';
    const lastInitial = lastName ? lastName.charAt(0) : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
};

const pastelColors = [
    '#ff5050', '#9999ff', '#00cc66', '#6699ff', '#33cccc', '#8585ad', '#669999', '#ffb84d'
];

const getColorForUser = (userId) => {
    return pastelColors[userId % pastelColors.length];
};

const timeSince = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutes ago";
    return Math.floor(seconds) + " seconds ago";
};

const Review = ({ review, renderStars }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    return (
        <div className={styles.review}>
            <div className={styles.reviewCardHeader}>
                <div className={styles.reviewerProfilePic} style={{ backgroundColor: getColorForUser(review.userId) }}>
                    {getInitials(review.firstName, review.lastName)}
                </div>
                <div className={styles.reviewCardHeaderInfo}>
                    <span className={styles.reviewerName}>{`${review.firstName} ${review.lastName}`}</span>
                </div>
            </div>
            <div className={styles.reviewCardRating}>
                {renderStars(review.overallRating)}
                <span className={styles.ratingValue}>{review.overallRating}</span>
                <span className={styles.reviewDate}>{timeSince(review.dateAdded)}</span>
            </div>
            <p>{review.description}</p>
            {review.mediaUrl && Array.isArray(review.mediaUrl) && review.mediaUrl.length > 0 && (
                <div className={styles.reviewImages}>
                    {review.mediaUrl.map((media, index) => (
                        media.url && <img key={media.id} src={media.url} alt={`Review thumbnail`} className={styles.reviewImageThumbnail} />
                    ))}
                </div>
            )}
        </div>
    );
};

const BusinessProfilePopup = ({ businessId, onClose }) => {
    const [businessProfile, setBusinessProfile] = useState(null);
    const [services, setServices] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [qualifications, setQualifications] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [portfolio, setPortfolio] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [isSaved, setIsSaved] = useState(false);
    const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
    const [businessStats, setBusinessStats] = useState(null);


    const navigate = useNavigate();

    // --- MASKING HELPERS (Consistent with other components) ---
    const getMaskedPhone = (phone) => {
        if (!phone) return '04** *** ***';
        return `${phone.substring(0, 4)} *** ***`; 
    };

    const getMaskedAddress = (fullAddress) => {
        if (!fullAddress) return 'Location Hidden';
        const postcodeMatch = fullAddress.match(/\b\d{4}\b/); 
        const stateMatch = fullAddress.match(/\b(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)\b/i);
        const state = stateMatch ? stateMatch[0].toUpperCase() : '';
        const postcode = postcodeMatch ? postcodeMatch[0] : '';
        if (state || postcode) return `****, ${state} ${postcode}`;
        return '**** (Address Protected)';
    };

    // --- ACTIONS ---
    const handleEnquiryClick = () => {
        // onClose(); // Optional: Close popup if you want them to focus entirely on the new page
        navigate(`/contact-business?businessId=${businessProfile.businessId}`);
    };

    const handlePhoneClick = () => {
        if (!businessProfile.isVerified) {
            handleEnquiryClick();
        } else {
            window.location.href = `tel:${businessProfile.phone}`;
        }
    };

    const handleDirectionsClick = () => {
        if (!businessProfile.isVerified) {
            handleEnquiryClick();
            return;
        }

        const primaryLocation = locations.find(loc => loc.isPrimary);
        if (primaryLocation && primaryLocation.address) {
            const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(primaryLocation.address)}`;
            window.open(url, '_blank');
        } else {
            toast.error("Address not available.");
        }
    };



    const checkIfSaved = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const decodedToken = jwtDecode(token);
            const userType = decodedToken.UserType;

            //const homeUserId = userType === 'Home' ? decodedToken.nameid : 0;
            //const bizUserId = userType === 'Business' ? decodedToken.nameid : 0;
            //const homeUserId = parseInt(localStorage.getItem('homeUserId'), 10) || 0;
            //const bizUserId  = parseInt(localStorage.getItem('businessId'), 10) || 0;

            const homeUserId = parseInt(localStorage.getItem('loggedInHomeUserId'), 10) || 0;
            const bizUserId  = parseInt(localStorage.getItem('loggedInBusinessId'), 10) || 0;


            
            const params = new URLSearchParams({
                savedBusinessId: businessId,
                userType,
            });

            if (userType === 'Home') {
                params.append('homeUserId', homeUserId);
            } else {
                params.append('businessId', bizUserId);
            }

            const { data } = await axios.get(
                `${API_BASE_URL}/api/BusinessProfile/isBusinessFavourite?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (typeof data.isFavourite === 'boolean') {
                setIsSaved(data.isFavourite);
            }
        } catch (err) {
            console.error('Error checking favourite status', err);
        }
    };


    useEffect(() => {
        if (!businessId) return;
        const fetchBusinessData = async () => {
            setLoading(true);
            try {
                const [profileResponse, reviewsResponse] = await Promise.all([
                    api.get(`/api/BusinessProfile/getBusinessProfile?businessId=${businessId}&logView=true`),
                    api.get(`/api/BusinessReview/getReviewsByBusinessId?businessId=${businessId}&baseUrl=base`)
                ]);

                const profileData = profileResponse.data;
                setBusinessProfile(profileData.businesses);
                setServices(profileData.services || []);
                setCertifications(profileData.certifications || []);
                setQualifications(profileData.qualificationDtos || []);
                setPortfolio(profileData.workPortfolio?.model || []);
                setLocations(profileData.locations || []);
                setBusinessStats(profileData.statistics || []);
                
                const reviewsData = reviewsResponse.data;
                if (reviewsData && Array.isArray(reviewsData.model)) {
                    setReviews(reviewsData.model);
                } else {
                    setReviews([]);
                }
                // Check saved status logic here if needed
            } catch (error) {
                console.error('Failed to fetch business data:', error);
                setReviews([]);
            } finally {
                setLoading(false);
            }
        };
        fetchBusinessData();
    }, [businessId]);



    const handleSaveFavourite = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error("You must be logged in to save a business.");
                return;
            }
            const decodedToken = jwtDecode(token);
            const userType = decodedToken.UserType;

            const loggedInHomeUserId  = parseInt(localStorage.getItem('loggedInHomeUserId'), 10)  || 0;
            const loggedInBusinessId  = parseInt(localStorage.getItem('loggedInBusinessId'), 10)  || 0;

            //const homeUserId = userType === 'Home' ? decodedToken.nameid : 0;
            //const businessUserId = userType === 'Business' ? decodedToken.nameid : 0;

            const payload = {
                savedBusinessId: businessId,
                homeUserId: userType === 'Home' ? loggedInHomeUserId : 0,
                businessId: userType === 'Business' ? loggedInBusinessId : 0,
                userType,
                commend: ''
            };

            const { data } = await api.post(
                `api/BusinessProfile/toggleBusinessFavourite`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (data.status === 'saved' || data.status === 'removed') {
                const newIsSaved = data.status === 'saved';
                setIsSaved(newIsSaved);
                toast.success(newIsSaved ? 'Added to your favourites' : 'Removed from your favourites');
            } else {
                toast.error('Unexpected response from server.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Could not update favourite status.');
        }
    };


    
    const handleMessageClickConcise = async (e, otherUser) => {
        setLoading(true);
        try {
    
            console.log(`otherUser.businessId = ${otherUser.businessId}`);
            console.log(`otherUser.businessUserId = ${otherUser.businessUserId}`);
    
    
            // 1. Grab your auth token, decode it:
            const token = localStorage.getItem('token');
            if (!token) {
            navigate('/login');
            return;
            }
            const { nameid: currentUserId, UserType } = jwtDecode(token);


            const myuserType = localStorage.getItem('userType');
            let url = "";


            // 2. Build the three query params that NewConversation expects:
            //    - always need senderBusinessUserId
            //    - supply either homeUserId OR receiverBusinessUserId
            const isHome = myuserType === 'Home';
            const senderBusinessUserId = isHome
            ? otherUser.businessUserId    // if I'm a Home‐user, I pass the Business I'm talking to
            : currentUserId;              // otherwise I (a Business) pass my own ID
    
            const homeUserId = isHome
            ? currentUserId               // Home user passes themselves
            : undefined;
    
            const receiverBusinessUserId = !isHome
            ? otherUser.businessUserId    // Business talking to another Business
            : undefined;
    
            console.log(`currentUserId = ${currentUserId}`);
            console.log(`senderBusinessUserId = ${senderBusinessUserId}`);
            console.log(`receiverBusinessUserId = ${receiverBusinessUserId}`);
            console.log(`homeUserId = ${homeUserId}`);
    
    
            // 3. Assemble and call the NewConversation endpoint:
            //senderBusinessUserId=123&homeUserId=456

            if (myuserType == "Home")
            {
                url = `senderBusinessUserId=${senderBusinessUserId}&homeUserId=${homeUserId}`;
            }
            else
            {
                url = `senderBusinessUserId=${senderBusinessUserId}&receiverBusinessUserId=${receiverBusinessUserId}`;                
            }

            const { data } = await api.get(
            `/api/Chat/NewConversation?${url}`
            );
    
            // 4. Pull out the conversationId and open the right route in a new tab
            const { conversationId } = data;
            const path = isHome
            ? `/home_messages?conversationId=${conversationId}`
            : `/business_messages?conversationId=${conversationId}`;
    
            window.open(path, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('Error starting conversation:', err);
        } finally {
            setLoading(false);
        }
    };

    

    const getDirections = () => {
        // 1. Find the primary location from the locations state array.
        const primaryLocation = locations.find(loc => loc.isPrimary);

        // 2. Ensure a primary location with an address exists.
        if (primaryLocation && primaryLocation.address) {
            // 3. Use the full address from the primary location object.
            const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(primaryLocation.address)}`;
            window.open(url, '_blank');
        } else {
            // 4. Handle cases where no primary address is found.
            console.error("No primary address found for directions.");
            toast.error("Primary business address not available.");
        }
    };



    if (loading) return <div className={styles.profileContainer}><Loader /></div>;
    if (!businessProfile) return <div className={styles.profileContainer}><p>No business profile found.</p></div>;

    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const halfStar = rating % 1 !== 0;
        const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
        return (
            <>
                {[...Array(fullStars)].map((_, i) => <Star key={`full-${i}`} size={16} fill="gold" color="gold" />)}
                {halfStar && <Star size={16} fill="gold" color="gold" style={{ clipPath: 'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }} />}
                {[...Array(emptyStars)].map((_, i) => <Star key={`empty-${i}`} size={16} color="gray" />)}
            </>
        );
    };

    // Helper to get display address based on verification
    const primaryLocation = locations.find(loc => loc.isPrimary);
    const isVerified = businessProfile.isVerified;

    const displayAddress = businessProfile.isVerified 
        ? primaryLocation?.address 
        : (primaryLocation?.address ? primaryLocation.address.split(',').slice(1).join(',').trim() + "" : "");

    const headerStyle = businessProfile.coverImage 
        ? { 
            backgroundImage: `url(${businessProfile.coverImage})`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center',
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            paddingLeft: '20px',
            paddingBottom: '20px',
            height: '200px'
          } 
        : {};

    const profilePicStyle = businessProfile.coverImage
        ? {
            width: '120px',
            height: '120px',
            objectFit: 'cover',
            borderRadius: '50%',
            border: '4px solid white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
        : {};

    return (
        <div className={styles.profileContainer}>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className={`${styles.profileHeader} ${businessProfile.coverImage ? styles.hasCover : ''}`} style={headerStyle}>
                <img 
                    src={businessProfile.profilePic} 
                    alt={businessProfile.businessName} 
                    className={styles.profilePic} 
                    style={profilePicStyle}
                />
                
                {/* --- SAVE BUTTON --- */}
                <button 
                    onClick={handleSaveFavourite} 
                    className={styles.headerSaveButton}
                    title={isSaved ? "Remove from Saved" : "Save Business"}
                >
                    {/* Fill icon if saved */}
                    <Bookmark size={18} fill={isSaved ? '#f59e0b' : 'none'} color={isSaved ? '#f59e0b' : '#64748b'} />
                </button>
                
                
                <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
            </div>
            
            <div className={styles.profileBody}>
                <div className={styles.tabContainer}>
                    <button className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                    <button className={`${styles.tabButton} ${activeTab === 'reviews' ? styles.activeTab : ''}`} onClick={() => setActiveTab('reviews')}>Reviews</button>
                    <button className={`${styles.tabButton} ${activeTab === 'portfolio' ? styles.activeTab : ''}`} onClick={() => setActiveTab('portfolio')}>Portfolio</button>
                </div>

                {activeTab === 'overview' && (
                    <div>
                        <div className={styles.businessDetails}>
                            
                            {/* --- HEADER: Name + Verified Badge --- */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '5px'
                                }}
                            >
                                <h2 className={styles.businessName} style={{ marginBottom: 0 }}>
                                    {businessProfile.businessName}
                                </h2>

                                {businessProfile.isDocApproved && isVerified ? (
                                    <VerifiedPill />
                                ) : (
                                    !isVerified && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                background: '#f1f5f9',
                                                color: '#64748b',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '10px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            <Lock size={10} /> <span>Privacy Protected</span>
                                        </div>
                                    )
                                )}
                            </div>




                            <div className={styles.ratingsSection}>
                                {businessStats && businessStats.averageRating > 0 && (
                                    <div className={styles.ratingLine}>
                                        {renderStars(businessStats.averageRating)}
                                        <span className={styles.ratingText}>
                                            Rating: {businessStats.averageRating.toFixed(1)} ({businessStats.reviewCount} reviews)
                                        </span>
                                    </div>
                                )}
                            </div>

 



                            {/* --- MASKED DETAILS (Lead Trap) --- */}
                            <div className={styles.address}>
                                <MapPin size={16} />
                                <span 
                                    onClick={!isVerified ? handleEnquiryClick : undefined}
                                    style={!isVerified ? {cursor: 'pointer', fontFamily: 'monospace', color: '#64748b'} : {}}
                                >
                                    {isVerified && primaryLocation ? primaryLocation.address : getMaskedAddress(primaryLocation?.address)}
                                </span>
                            </div>

                            <div className={styles.phone}>
                                <Phone size={16} />
                                <span
                                    onClick={!isVerified ? handleEnquiryClick : undefined}
                                    style={!isVerified ? {cursor: 'pointer'} : {}}
                                >
                                    {isVerified ? businessProfile.phone : getMaskedPhone(businessProfile.phone)}
                                </span>
                            </div>

                            {isVerified && businessProfile.website && (
                                <div className={styles.phone}>
                                    <Globe size={16} />
                                    <span>{businessProfile.website}</span>
                                </div>
                            )}

                            <div className={styles.professions}>
                                {businessProfile.professions?.map(p => <span key={p.professionId} className={styles.professionBadge}>{p.name}</span>)}
                            </div>
                        </div>

                        {/* --- REDESIGNED ACTION BUTTONS --- */}
                        <div className={styles.actionButtons}>
                            
                            {/* 1. WEBSITE (Only if Verified) */}
                            {isVerified && businessProfile.website && (
                                <button className={styles.actionButton} onClick={() => window.open(businessProfile.website, '_blank')}>
                                    <div className={styles.iconCircle}><Globe size={24} /></div>
                                    <span>Website</span>
                                </button>
                            )}

                            {/* 2. PHONE (Trap or Call) */}
                            <button className={styles.actionButton} onClick={handlePhoneClick}>
                                <div className={styles.iconCircle} style={!isVerified ? {background: '#f1f5f9', color: '#64748b'} : {}}>
                                    {isVerified ? <Phone size={24} /> : <Lock size={24} />}
                                </div>
                                <span>{isVerified ? 'Call Now' : 'Show No.'}</span>
                            </button>

                            {/* 3. MESSAGE (Always available) */}
                            <button className={styles.actionButton} onClick={(e) => handleMessageClickConcise(e, businessProfile)}>
                                <div className={styles.iconCircle}><MessageSquare size={24} /></div>
                                <span>Message</span>
                            </button>

                            {/* 4. DIRECTIONS (Trap or Map) */}
                            <button className={styles.actionButton} onClick={handleDirectionsClick}>
                                <div className={styles.iconCircle} style={!isVerified ? {background: '#f1f5f9', color: '#64748b'} : {}}>
                                    {isVerified ? <MapPin size={24} /> : <Lock size={24} />}
                                </div>
                                <span>{isVerified ? 'Directions' : 'Show Map'}</span>
                            </button>

                        </div>




                        {/* ... Rest of content (About, Info, Services) ... */}
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>About us</h3>
                            <p>{businessProfile.description || "No description available."}</p>
                        </div>
                        
                        {/* More sections... */}
                        {businessProfile.isVerified && businessProfile.website && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Business Info</h3>
                            
                            <p><strong>ABN:</strong> {businessProfile.abn}</p>
                            <p><strong>Licence Number:</strong> {businessProfile.licenseNumber}</p>
                            <p><strong>Registration Number:</strong> {businessProfile.registrationNumber}</p>
                            
                        </div>
                        )}

                        {locations.length > 1 && (
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Other Locations</h3>
                                <div className={styles.grid}>
                                    {locations.filter(loc => !loc.isPrimary).map(location => (
                                        <div key={location.locationId} className={styles.gridItem}>
                                            <MapPin size={16} className={styles.icon} />
                                            <span>{location.address}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>Badges</h3>
                            <div className={styles.badgeContainer}>
                                {businessProfile.fullyInsured && <div className={styles.badge}><CheckCircle size={14}/> Fully Insured</div>}
                                {businessProfile.yearsExperience > 0 && <div className={styles.badge}><Award size={14}/> {businessProfile.yearsExperience} Years Experience</div>}
                                {businessProfile.afterHours !== 0 && <div className={styles.badge}><Clock size={14}/> After Hours</div>}
                                {businessProfile.weekends !== 0 && <div className={styles.badge}><Sun size={14}/> Weekends</div>}
                            </div>
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}><ListChecks size={20} /> Services Offered</h3>
                            <div className={styles.grid}>
                                {services.map(service => (
                                    <div key={service.serviceId} className={styles.gridItem}>
                                        <ListChecks size={16} className={styles.icon} />
                                        <span>{service.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}><CheckCircle size={20} /> Certifications</h3>
                            <div className={styles.grid}>
                                {certifications.map(cert => (
                                    <div key={cert.certificationId} className={styles.gridItem}>
                                        <CheckCircle size={16} className={styles.icon} />
                                        <span>{cert.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}><Award size={20} /> Qualifications</h3>
                            <div className={styles.grid}>
                                {qualifications.map(qual => (
                                    <div key={qual.qualificationId} className={styles.gridItem}>
                                        <Award size={16} className={styles.icon} />
                                        <span>{qual.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>




                    </div>
                )}

                {/* Reviews and Portfolio Tabs ... (Unchanged) */}
                {activeTab === 'reviews' && (
                    <div className={styles.reviewsSection}>
                        {reviews.length > 0 ? reviews.map(r => <Review key={r.reviewId} review={r} renderStars={renderStars} />) : <p>No reviews.</p>}
                    </div>
                )}
                {activeTab === 'portfolio' && (
                    <div className={styles.portfolioSection}>
                        {portfolio.length > 0 ? portfolio.map(item => (
                            <div key={item.portfolioId} className={styles.portfolioCard} onClick={() => setSelectedPortfolioItem(item)}>
                                <img src={item.mediaUrl?.[0]?.url} className={styles.portfolioImage} alt="" />
                                <div className={styles.portfolioCardContent}><h4>{item.title}</h4></div>
                            </div>
                        )) : <p>No portfolio items.</p>}
                    </div>
                )}
                
                {selectedPortfolioItem && <PortfolioLightbox portfolioItem={selectedPortfolioItem} onClose={() => setSelectedPortfolioItem(null)} />}
            </div>
        </div>
    );
};

export default BusinessProfilePopup;
